import { Fragment } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import _filter from 'lodash/filter';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import CardModule from '../../shared/card-module';
import TableBasic from '../../../atoms/table-basic/index';
import format from '../../../../utils/format';
import styles from './underlying-investments.module.scss';
import useProfileAppContext from '../../../../stores/profile/use-profile-app-context';
import useUnderlyingInvestments from '../../../../stores/profile/modules/use-underlying-investments';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { getUnderlyingInvestmentLink } from '../../../../utils/underlying-investment-helper';
import { FUND_TYPES } from '../../../../stores/profile/get-fund-type';
// import usePerformanceReturnsStore from '../../../../stores/profile/modules/use-performance-returns';

const className = augmentClassName(styles);

const renderSuperscript = ({ fundPerformanceData, shareClass, fundType }) => {
  if (
    (fundType === FUND_TYPES.variableAnnuityCref && shareClass === 'Class R3') ||
    !format.isValid(fundPerformanceData)
  ) {
    return null;
  }
  return '*';
};

const getShowSuperscript = ({ inceptionDate, asOfDate, cellIndex, shareClass }) => {
  let showSuperscript = false;
  const IsLinkedPerf1Yr =
    new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 1) > asOfDate;
  const IsLinkedPerf3Yr =
    new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 3) > asOfDate;
  const IsLinkedPerf5Yr =
    new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 5) > asOfDate;
  const IsLinkedPerf10Yr =
    new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 10) > asOfDate;
  showSuperscript =
    (cellIndex === 1 && IsLinkedPerf1Yr) ||
    (cellIndex === 2 && IsLinkedPerf3Yr) ||
    (cellIndex === 3 && IsLinkedPerf5Yr) ||
    (cellIndex === 4 && IsLinkedPerf10Yr) ||
    (cellIndex === 5 &&
      (shareClass === 'Class R1' ||
        shareClass === 'Class R2' ||
        shareClass === 'Class R3' ||
        shareClass === 'Class R4'));

  return showSuperscript;
};

const renderLinkedPerformance = (fund, fundPerformanceData, cellIndex) => {
  const inceptionDate = new Date(fund.inceptionDate);
  const asOfDate = new Date(fund.asOfDate); //TIAACrefMonthlyFundPerf.perfDate, keep it consistent with legacy research
  const shareClass = fund?.shareClass;
  const shouldShowSuperscript = getShowSuperscript({
    inceptionDate,
    asOfDate,
    cellIndex,
    shareClass
  });
  const rowValue = format.number(fundPerformanceData, {
    formatString: '0.00'
  });
  const fundType = fund.fundType;

  return (
    <>
      {rowValue === '0.00' ? '0.00' : rowValue}
      {shouldShowSuperscript && renderSuperscript({ fundPerformanceData, shareClass, fundType })}
    </>
  );
};

const renderTableCells = (fund, channel) => {
  return (
    <Fragment>
      <td>
        <a
          tabIndex={-1}
          target="_blank"
          rel="noopener noreferrer"
          {...className('underlying-investment-link')}
          href={getUnderlyingInvestmentLink(
            window.location.hostname,
            channel,
            fund.xid,
            fund.fundType
          )}
        >
          {format.text(fund.fundName)}
        </a>
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {format.number(fund.allocation)}
      </td>
      <td {...className('align-middle', 'p-0')}>
        <ProgressBar
          bsPrefix="md-progress"
          variant="custom"
          now={fund.allocation}
          min={0}
          max={100}
        />
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {format.date(fund.inceptionDate)}
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {renderLinkedPerformance(fund, fund.returnY1, 1)}
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {renderLinkedPerformance(fund, fund.returnY3, 2)}
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {renderLinkedPerformance(fund, fund.returnY5, 3)}
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {renderLinkedPerformance(fund, fund.returnY10, 4)}
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {renderLinkedPerformance(fund, fund.returnSI, 5)}
      </td>
      <td {...className('text-right', 'font-weight-bold', 'align-middle')}>
        {format.number(fund.grossExpenseRatio)} / {format.number(fund.netExpenseRatio)}
      </td>
    </Fragment>
  );
};

//TODO: not the best way to go about this, should update this later
const renderScreenReaderTable = (funds, channel) => {
  return funds.map((fund, index) => {
    return (
      <div data-key={`${fund.fundName || ''}-${index}`} key={`${fund.fundName || ''}-${index}`}>
        <TableBasic>
          <thead>
            <tr>
              <th>{fund.fundType}</th>
              <th>Allocation (%)</th>
              <th>Inception Date</th>
              <th>1 Year</th>
              <th>3 Year</th>
              <th>5 Year</th>
              <th>10 Year</th>
              <th>Since Inception</th>
              <th>Gross / Net Expense Ratio (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={getUnderlyingInvestmentLink(
                    window.location.hostname,
                    channel,
                    fund.xid,
                    fund.fundType
                  )}
                >
                  {format.text(fund.fundName)}
                </a>
              </td>
              <td>{format.number(fund.allocation)}</td>
              <td>{format.date(fund.inceptionDate)}</td>
              <td>{format.number(fund.returnY1)}</td>
              <td>{format.number(fund.returnY3)}</td>
              <td>{format.number(fund.returnY5)}</td>
              <td>{format.number(fund.returnY10)}</td>
              <td>{format.number(fund.returnSI)}</td>
              <td>
                {format.number(fund.grossExpenseRatio)} / {format.number(fund.netExpenseRatio)}
              </td>
            </tr>
          </tbody>
        </TableBasic>
        {fund.fundType === 'TIAA Fixed Annuity' && (
          <div>
            Current Rate: {format.number(fund.rateCurrent)}
            Guarenteed Rate: {format.number(fund.rateMinimumGuaranteed)}
          </div>
        )}
        {fund.assetClass === 'Money Market' && (
          <div>
            7 Day Current Annualized Yield: {format.percent(fund['7DayYieldCurrent'])}
            as of {format.date(fund['7DayYieldAsOfDate'])}7 Day Effective Annualized Yield:{' '}
            {format.percent(fund['7DayYieldEffective'])}
            as of {format.date(fund['7DayYieldAsOfDate'])}
          </div>
        )}
        {fund.fundType === 'TIAA Stable Value' && (
          <div>
            Net Crediting Rate: {format.number(fund.netCreditingRate)}
            Declared Crediting Rate: {format.number(fund.declaredCreditingRate)}
            Total Contract Fee: {format.number(fund.totalContractFee)}
            Minimum Guaranteed Rate: {format.number(fund.rateMinimumGuaranteed)}
          </div>
        )}
      </div>
    );
  });
};

const renderFixedAnnuityOrTSVTable = (funds, isTSV, channel) => {
  const fundToDisplay = funds[0];
  return (
    <tbody {...className('border-top-0')}>
      <tr>
        <th {...className('font-weight-bold', 'text-black', 'border-top-0')}>Fixed Annuities</th>
      </tr>
      <tr {...className('border-bottom')}>{renderTableCells(fundToDisplay, channel)}</tr>
      <tr>
        <td colSpan={10}>
          {isTSV ? (
            <div {...className('text-right')}>
              <div {...className('font-weight-normal', 'font-size-11', 'text-muted')}>
                Net Crediting Rate: {format.number(fundToDisplay.netCreditingRate)}
              </div>
              <div {...className('font-weight-normal', 'font-size-11', 'text-muted')}>
                Declared Crediting Rate: {format.number(fundToDisplay.declaredCreditingRate)}
              </div>
              <div {...className('font-weight-normal', 'font-size-11', 'text-muted')}>
                Total Contract Fee: {format.number(fundToDisplay.totalContractFee)}
              </div>
              <div {...className('font-weight-normal', 'font-size-11', 'text-muted')}>
                Minimum Guaranteed Rate: {format.number(fundToDisplay.rateMinimumGuaranteed)}
              </div>
            </div>
          ) : (
            <div {...className('text-right')}>
              <div {...className('font-weight-normal', 'font-size-11', 'text-muted')}>
                Current Rate: {format.number(fundToDisplay.rateCurrent)}
              </div>
              <div {...className('font-weight-normal', 'font-size-11', 'text-muted')}>
                Guaranteed Rate: {format.number(fundToDisplay.rateMinimumGuaranteed)}
              </div>
            </div>
          )}
        </td>
      </tr>
    </tbody>
  );
};

const UnderlyingInvestments = () => {
  const {
    asOfDate,
    fixedAnnuityFunds,
    fundsToDisplay,
    fundTypesToDisplay,
    moneyMarketDisclosure,
    tsvFunds
  } = useUnderlyingInvestments();

  const { channel } = useProfileAppContext();

  return (
    <CardModule
      data-analytics-componenttype="full-width-component"
      data-analytics-id="FW_UnderlyingInvestments"
      data-analytics-section="component"
      {...className('underlying-investments-table')}
    >
      <CardModule.Header>
        Underlying Investment Breakdown
        <CardModule.AsOfDate>{format.date(asOfDate)}</CardModule.AsOfDate>
      </CardModule.Header>
      <div {...className('sr-only')}>
        {renderScreenReaderTable([...fundsToDisplay, ...fixedAnnuityFunds, ...tsvFunds], channel)}
      </div>
      <div {...className('table-scroll')}>
        <CardModule.Body aria-hidden="true">
          <TableBasic>
            {fundTypesToDisplay.map((fundTypeRaw, fundTypeIndex) => {
              const fundsByType = _filter(fundsToDisplay, fund => {
                return fund.fundType === fundTypeRaw;
              });
              return (
                <Fragment
                  data-key={`${fundTypeRaw || ''}-${fundTypeIndex}`}
                  key={`${fundTypeRaw || ''}-${fundTypeIndex}`}
                >
                  {fundTypeIndex === 0 ? (
                    <thead>
                      <tr>
                        <th {...className('border-0')} role="presentation" colSpan={4}></th>
                        <th
                          colSpan={5}
                          {...className('header-category', 'text-center', 'border-0')}
                        >
                          <div>Average Annualized Total Returns (%)</div>
                        </th>
                      </tr>
                      <tr>
                        <th {...className('w-35')}>{fundTypeRaw}</th>
                        <th {...className('text-right', 'w-9')}>Allocation (%)</th>
                        {/*Allocation progress bar*/}
                        <th {...className('w-6')} role="presentation"></th>
                        <th {...className('text-right', 'w-10')}>Inception Date</th>
                        <th {...className('text-right', 'w-5')}>1 Yr</th>
                        <th {...className('text-right', 'w-5')}>3 Yr</th>
                        <th {...className('text-right', 'w-5')}>5 Yr</th>
                        <th {...className('text-right', 'w-5')}>10 Yr</th>
                        <th {...className('text-right', 'w-10')}>Since Inception</th>
                        <th {...className('text-right', 'w-10')}>Gross / Net Exp. Ratio (%)</th>
                      </tr>
                    </thead>
                  ) : null}
                  <tbody {...className('border-top-0')}>
                    {fundsByType.map((fund, fundIndex) => {
                      return (
                        <Fragment
                          data-key={`${fund.fundName || ''}-${fundIndex}`}
                          key={`${fund.fundName || ''}-${fundIndex}`}
                        >
                          {fundTypeIndex !== 0 && (
                            <>
                              <tr role="presentation"></tr>
                              <tr {...className('border-bottom')}>
                                <th
                                  {...className('border-top-0', 'font-weight-bold', 'text-black')}
                                >
                                  {fundTypeRaw}
                                </th>
                              </tr>
                            </>
                          )}
                          <tr
                            {...className(
                              fundIndex === fundsByType.length - 1 ? 'border-bottom' : ''
                            )}
                          >
                            {renderTableCells(fund, channel)}
                          </tr>
                          {fund.assetClass === 'Money Market' && (
                            <tr>
                              <td colSpan={10}>
                                <div {...className('text-right')}>
                                  <div
                                    {...className(
                                      'font-weight-normal',
                                      'font-size-11',
                                      'text-muted'
                                    )}
                                  >
                                    7-day current annualized yield:{' '}
                                    {format.percent(fund['7DayYieldCurrent'])} as of{' '}
                                    {format.date(fund['7DayYieldAsOfDate'])}
                                  </div>
                                  <div
                                    {...className(
                                      'font-weight-normal',
                                      'font-size-11',
                                      'text-muted'
                                    )}
                                  >
                                    7-day effective annualized yield:{' '}
                                    {format.percent(fund['7DayYieldEffective'])} as of{' '}
                                    {format.date(fund['7DayYieldAsOfDate'])}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          {/*Spacer row for tables that follow*/}
                          {
                            // prettier-ignore
                            fundTypeIndex === fundTypesToDisplay.length - 1 &&
                            fundIndex === fundsByType.length - 1 &&
                            (
                              <tr role="presentation" {...className('border-bottom-0')}></tr>
                            )
                          }
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Fragment>
              );
            })}
            {tsvFunds.length > 0 ? renderFixedAnnuityOrTSVTable(tsvFunds, true, channel) : null}
            {fixedAnnuityFunds.length > 0
              ? renderFixedAnnuityOrTSVTable(fixedAnnuityFunds, false, channel)
              : null}
          </TableBasic>
        </CardModule.Body>
        <CardModule.Footer {...className('print-table-footer')}>
          <ButtonDisclosures module={'underlying-investments'} />
          {moneyMarketDisclosure && (
            <p {...className('inline-disclosure-text', 'font-weight-bold', 'mt-2')}>
              {moneyMarketDisclosure}
            </p>
          )}
        </CardModule.Footer>
      </div>
    </CardModule>
  );
};

export default UnderlyingInvestments;
